import { TypographyVariantsOptions } from "@mui/material/styles";

const LAYOUT_GAP = 3;

/**
 * The gap between one dashboard section and the next.
 *
 * Wider than `SECTION_GAP`, which spaces a page's own blocks: a dashboard
 * section is a whole band of cards, and at 16px the bands ran together into
 * one field. The design sets 22 here and 16 there, and the difference is what
 * makes the sections read as separate.
 */
const DASHBOARD_SECTION_GAP = 22;

const sharedNeutrals = {
  black: "#0A0D12",
  transparent: "transparent",
  white: "#FFFFFF"
} as const;

/**
 * Four corners, and every rounded thing picks one.
 *
 * Softened by a step across the board: the page had 24, 22, 12, 10 and 8 all
 * visible at once, and at those radii a card reads as a pill rather than as a
 * panel. Each step is now a size apart rather than two pixels, so the nesting
 * is legible — a button (8) inside a card (10) inside the band (18) — and
 * nothing lands between two steps.
 *
 * `borderRadiusButton` is the one every CONTROL takes: buttons, icon boxes,
 * nav rows, the rail tiles. It exists so a button and the icon box beside it
 * cannot drift apart, which is exactly what had happened (10 and 12).
 */
const shape = {
  borderRadius: 6,
  borderRadiusButton: 8,
  borderRadiusLg: 18,
  // The design's card radius, and the one every control that is not a button
  // takes — search boxes, toggle groups, dropdown panels. It was 16, which
  // against the band's 22 and the controls' 12 gave the page three different
  // curves and no reason for any of them.
  borderRadiusMd: 10
} as const;

/**
 * Size of every control (buttons, icon buttons, search, toggle groups),
 * read as `theme.controls`. Its corner is `shape.borderRadiusButton`.
 *
 * Heights are outer (border-box). Form fields keep their own 46px height
 * but use the control corner.
 */
const CONTROL_HEIGHT = 32;
/** Toggle group track padding; the track also has a 1px border. */
const SEGMENT_INSET = 2;

const controls = {
  /** The table-cell step. Its corner is `shape.borderRadius`. */
  compact: {
    height: 28,
    paddingX: 8
  },
  height: CONTROL_HEIGHT,
  /** Between a glyph and its label. */
  iconGap: 8,
  iconSize: 16,
  /** A labelled button's side padding. */
  paddingX: 14,
  /**
   * Segment inside a `height`-tall toggle track: less padding and border on
   * each side, with a concentric corner.
   */
  segment: {
    height: CONTROL_HEIGHT - 2 * (SEGMENT_INSET + 1),
    inset: SEGMENT_INSET,
    radius: shape.borderRadiusButton - SEGMENT_INSET
  }
} as const;

type ControlTokens = typeof controls;

/**
 * The only font weights the app uses. Named tokens instead of raw numbers so
 * every call site picks from the same four, the same way font sizes pick from
 * a named variant instead of a raw pixel value.
 */
const FONT_WEIGHT = {
  bold: 700,
  medium: 500,
  regular: 400,
  semiBold: 600
} as const;

type FontWeightToken = keyof typeof FONT_WEIGHT;

// Line heights are unitless multipliers (MUI's own convention), not px. A
// unitless value scales with whatever font-size is actually rendered — so a
// caller that overrides a variant's fontSize via sx (without also overriding
// lineHeight) still gets a proportional line-height instead of inheriting a
// fixed px value sized for the variant's own default fontSize.
//
// Dashboard roles map onto these variants: a count badge is `badge`, a caption
// or table head `caption`, supporting text `body2`, a row or card's primary
// line `rowTitle`, a section or card heading `sectionTitle`, a statistic
// `figure`. Pick by role, not by the size that looks close.
const typography: TypographyVariantsOptions = {
  badge: {
    fontSize: "11px",
    fontWeight: FONT_WEIGHT.regular,
    lineHeight: 1.3
  },
  body1: {
    fontSize: "13px",
    fontWeight: FONT_WEIGHT.regular,
    lineHeight: 1.54
  },
  body2: {
    fontSize: "13px",
    fontWeight: FONT_WEIGHT.regular,
    lineHeight: 1.38
  },
  button: {
    fontSize: "13px",
    fontWeight: FONT_WEIGHT.medium,
    lineHeight: 1.38,
    textTransform: "none"
  },
  caption: {
    fontSize: "12px",
    fontWeight: FONT_WEIGHT.regular,
    lineHeight: 1.33
  },
  figure: {
    fontSize: "26px",
    fontWeight: FONT_WEIGHT.semiBold,
    lineHeight: 1.2
  },
  fontFamily: "Inter, sans-serif",
  // Explicit so `theme.typography.fontWeightX` is the one canonical way to
  // reference a weight anywhere `theme` is in scope — the same role
  // `theme.typography.<variant>.fontSize` plays for size. fontWeightSemiBold
  // doesn't exist on stock MUI; see types/overrides/createTypography.d.ts.
  fontWeightBold: FONT_WEIGHT.bold,
  fontWeightMedium: FONT_WEIGHT.medium,
  fontWeightRegular: FONT_WEIGHT.regular,
  fontWeightSemiBold: FONT_WEIGHT.semiBold,
  h1: { fontSize: "48px", fontWeight: FONT_WEIGHT.bold, lineHeight: 1.17 },
  h2: { fontSize: "42px", fontWeight: FONT_WEIGHT.bold, lineHeight: 1.19 },
  h3: {
    fontSize: "32px",
    fontWeight: FONT_WEIGHT.semiBold,
    lineHeight: 1.25
  },
  h4: {
    fontSize: "24px",
    fontWeight: FONT_WEIGHT.semiBold,
    lineHeight: 1.33
  },
  h5: {
    fontSize: "20px",
    fontWeight: FONT_WEIGHT.semiBold,
    lineHeight: 1.4
  },
  h6: {
    fontSize: "18px",
    fontWeight: FONT_WEIGHT.semiBold,
    lineHeight: 1.33
  },
  menuCaption: {
    color: "var(--mui-palette-text-secondary)",
    display: "block",
    fontSize: "10px",
    fontWeight: FONT_WEIGHT.semiBold,
    letterSpacing: "0.8px",
    marginBottom: 0,
    textTransform: "uppercase"
  },
  rowTitle: {
    fontSize: "14px",
    fontWeight: FONT_WEIGHT.regular,
    lineHeight: 1.3
  },
  sectionTitle: {
    fontSize: "15px",
    fontWeight: FONT_WEIGHT.semiBold,
    lineHeight: 1.3
  },
  subtitle1: {
    fontSize: "16px",
    fontWeight: FONT_WEIGHT.medium,
    lineHeight: 1.38
  },
  subtitle2: {
    fontSize: "12px",
    fontWeight: FONT_WEIGHT.medium,
    lineHeight: 1.5
  }
};

export {
  typography,
  shape,
  controls,
  sharedNeutrals,
  LAYOUT_GAP,
  DASHBOARD_SECTION_GAP,
  FONT_WEIGHT
};
export type { ControlTokens, FontWeightToken };
